import React, { useState, useEffect, useRef, memo, useMemo } from 'react';

// VoxSmyth Scroll Intro Component for first-time visitors
const VoxSmythScrollIntro = ({ onIntroComplete }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const onIntroCompleteRef = useRef(onIntroComplete);

  // Keep the callback ref updated
  useEffect(() => {
    onIntroCompleteRef.current = onIntroComplete;
  }, [onIntroComplete]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let ticking = false;
    let lastScrollY = 0;
    let lastProgress = 0;
    let lastSection = 0;
    let completionTriggered = false; // Flag to prevent multiple completion triggers
    let scrollEnabled = false; // Flag to prevent multiple scroll enabling
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Skip unnecessary updates if scroll hasn't changed significantly
      if (Math.abs(currentScrollY - lastScrollY) < 10 || completionTriggered) {
        return;
      }
      
      if (!ticking) {
        requestAnimationFrame(() => {
          if (!containerRef.current || completionTriggered) {
            ticking = false;
            return;
          }
          
          const scrollY = window.scrollY;
          const windowHeight = window.innerHeight;
          // Scroll height calculation for 500vh container
          const totalScrollHeight = windowHeight * 5; 
          const maxScrollableHeight = document.documentElement.scrollHeight - windowHeight;
          
          // Use the smaller of the two to handle edge cases where content is shorter than expected
          const effectiveScrollHeight = Math.min(totalScrollHeight, maxScrollableHeight);
          
          const progress = Math.min(scrollY / effectiveScrollHeight, 1);
          
          // Only update progress if there's a significant change (reduce re-renders)
          if (Math.abs(progress - lastProgress) > 0.005) {
            setScrollProgress(progress);
            lastProgress = progress;
          }
          
          // Only update section if it actually changed
          const newSection = progress < 0.33 ? 0 : progress < 0.66 ? 1 : 2;
          if (newSection !== lastSection) {
            setCurrentSection(newSection);
            lastSection = newSection;
          }
          
          // When scroll is complete, mark intro as seen and transition to main landing
          // Use a higher threshold and stronger flag protection
          if (progress >= 0.95 && !completionTriggered) {
            completionTriggered = true;
            console.log('🎯 VoxSmyth Intro: Scroll completion detected, progress:', progress);
            
            // Remove scroll listener immediately to prevent further triggers
            window.removeEventListener('scroll', handleScroll, { passive: true });
            
            setTimeout(() => {
              localStorage.setItem('voxsmyth-intro-seen', 'true');
              onIntroCompleteRef.current();
            }, 1000);
          }
          
          // Backup completion: if user has scrolled significantly and is near the end
          else if (progress >= 0.9 && scrollY > windowHeight * 4.5 && !completionTriggered) {
            console.log('🎯 VoxSmyth Intro: Backup scroll completion triggered at', progress);
            completionTriggered = true;
            
            // Remove scroll listener immediately
            window.removeEventListener('scroll', handleScroll, { passive: true });
            
            setTimeout(() => {
              localStorage.setItem('voxsmyth-intro-seen', 'true');
              onIntroCompleteRef.current();
            }, 1500);
          }
          
          lastScrollY = scrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    // Enable scrolling after a brief delay to avoid race conditions
    const enableScrollTimer = setTimeout(() => {
      if (!scrollEnabled) {
        scrollEnabled = true;
        document.body.style.overflow = 'auto';
        console.log('🎯 VoxSmyth Intro: Scroll enabled');
        
        // Force scroll container to be scrollable
        const scrollContainer = containerRef.current;
        if (scrollContainer) {
          // Ensure the container is actually creating scroll space
          scrollContainer.style.position = 'relative';
          scrollContainer.style.zIndex = '1';
          console.log('🎯 VoxSmyth Intro: Container scroll forced');
        }
      }
    }, 500); // Original delay

    // Add passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll, { passive: true });
      // Restore overflow without forcing if already correct
      if (document.body.style.overflow !== 'auto') {
        document.body.style.overflow = 'auto';
      }
      clearTimeout(enableScrollTimer);
    };
  }, []); // Remove onIntroComplete from dependencies to prevent re-execution

  // Failsafe: Auto-complete intro after 15 seconds if user doesn't scroll (reduced for better UX)
  useEffect(() => {
    const failsafeTimer = setTimeout(() => {
      console.log('🎯 VoxSmyth Intro: Auto-completing after 15 seconds for better UX');
      localStorage.setItem('voxsmyth-intro-seen', 'true');
      onIntroCompleteRef.current();
    }, 15000); // Reduced from 30 to 15 seconds

    return () => clearTimeout(failsafeTimer);
  }, []); // Remove onIntroComplete dependency

  // Memoize expensive calculations to prevent unnecessary re-renders
  const { overlayOpacity, text1Progress, text2Progress, text3Progress } = useMemo(() => {
    // Calculate opacity for video overlay based on scroll
    const overlayOpacity = Math.min(scrollProgress * 2, 0.9);
    
    // Text animations with updated progress for 3 sections instead of 4
    const text1Progress = scrollProgress < 0.15 ? 
      Math.min((scrollProgress * 6.67), 1) :                    // Fade in: 0-15%
      scrollProgress < 0.25 ? 1 :                               // Static: 15-25%
      scrollProgress < 0.33 ? Math.max(1 - ((scrollProgress - 0.25) * 12.5), 0) : 0;  // Fade out: 25-33%
      
    const text2Progress = scrollProgress >= 0.33 && scrollProgress < 0.45 ? 
      Math.min(((scrollProgress - 0.33) * 8.33), 1) :          // Fade in: 33-45%
      scrollProgress >= 0.45 && scrollProgress < 0.55 ? 1 :    // Static: 45-55%
      scrollProgress >= 0.55 && scrollProgress < 0.66 ? Math.max(1 - ((scrollProgress - 0.55) * 9.09), 0) : 0;  // Fade out: 55-66%
      
    const text3Progress = scrollProgress >= 0.66 && scrollProgress < 0.75 ? 
      Math.min(((scrollProgress - 0.66) * 11.11), 1) :         // Fade in: 66-75%
      scrollProgress >= 0.75 && scrollProgress < 0.9 ? 1 :     // Static: 75-90% (longer for final message)
      scrollProgress >= 0.9 && scrollProgress < 1 ? Math.max(1 - ((scrollProgress - 0.9) * 10), 0) : 0;  // Fade out: 90-100%

    return { overlayOpacity, text1Progress, text2Progress, text3Progress };
  }, [scrollProgress]);

  return (
    <>
      {/* Scroll Container - Reduced height for better UX */}
      <div 
        ref={containerRef}
        style={{ height: '500vh' }} // Revert to original height
      >
        {/* Fixed Video Background */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          zIndex: 1,
          background: `linear-gradient(135deg, 
            #1A202C 0%,
            #2D3748 25%,
            #1A202C 50%,
            #2D3748 75%,
            #1A202C 100%
          )`,
          backgroundSize: '400% 400%',
          animation: 'gradient-shift 8s ease infinite'
        }}>
          {/* Conditional rendering: CSS gradient for mobile (no heavy image), Video for desktop — defer load */}
          {isMobile ? (
            <div
              style={{
                width: '100%', height: '100%',
                background: 'radial-gradient(ellipse at center, #2D3748 0%, #1A202C 60%, #0F1117 100%)',
                opacity: 0.9,
              }}
            />
          ) : scrollProgress > 0.05 ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.8
              }}
              onLoadedData={() => {
                console.log('Video loaded successfully');
                if (videoRef.current) {
                  videoRef.current.play().catch(e => {
                    console.log('Video autoplay failed, user interaction required:', e);
                  });
                }
              }}
              onEnded={() => {
                // Ensure video loops by restarting if needed
                if (videoRef.current) {
                  videoRef.current.currentTime = 0;
                  videoRef.current.play().catch(e => {
                    console.log('Video restart failed:', e);
                  });
                }
              }}
              onError={(e) => {
                console.log('Video failed to load, using gradient background:', e);
                e.target.style.display = 'none';
              }}
            >
              <source src="/intro-video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : null}
          
          {/* Animated particles overlay for visual interest */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `radial-gradient(circle at 20% 80%, rgba(215, 179, 119, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(215, 179, 119, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 40% 40%, rgba(215, 179, 119, 0.05) 0%, transparent 30%)`,
            animation: 'float-particles 12s ease-in-out infinite'
          }} />
          
          {/* Dark Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#1A202C',
            opacity: overlayOpacity,
            transition: 'opacity 0.3s ease-out'
          }} />
        </div>

        {/* Text Overlays */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {/* Section 1: "Experience the Magic" */}
          <div style={{
            position: 'absolute',
            top: isMobile ? '40%' : '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: text1Progress,
            transition: 'opacity 0.5s ease-out'
          }}>
            <GoldenText 
              text={isMobile ? "Experience\nthe\nMagic" : "Experience the Magic"}
              progress={text1Progress}
              fontSize={isMobile ? "2.5rem" : "4rem"}
              textAlign="center"
              allowLineBreaks={true}
            />
          </div>

          {/* Section 2: "Multi-Voice Storytelling" & "Literary Excellence" */}
          <>
            <div style={{
              position: 'absolute',
              top: isMobile ? '25%' : '35%',
              left: isMobile ? '5%' : '10%',
              opacity: text2Progress,
              transition: 'opacity 0.5s ease-out'
            }}>
              <GoldenText 
                text="Multi-Voice Storytelling" 
                progress={text2Progress}
                fontSize={isMobile ? "1.8rem" : "3rem"}
                textAlign="left"
              />
            </div>
            
            <div style={{
              position: 'absolute',
              bottom: isMobile ? '25%' : '35%',
              right: isMobile ? '5%' : '10%',
              opacity: text2Progress,
              transition: 'opacity 0.5s ease-out'
            }}>
              <GoldenText 
                text="Literary Excellence" 
                progress={text2Progress}
                fontSize={isMobile ? "1.8rem" : "3rem"}
                textAlign="right"
              />
            </div>
          </>

          {/* Section 3: "Your story. Your cast. Your way." */}
          <div style={{
            position: 'absolute',
            top: isMobile ? '40%' : '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: text3Progress,
            transition: 'opacity 0.5s ease-out'
          }}>
            <GoldenText 
              text={isMobile ? "Your story.\nYour cast.\nYour way." : "Your Story. Your Cast. Your Way."}
              progress={text3Progress}
              fontSize={isMobile ? "2.2rem" : "3.5rem"}
              textAlign="center"
              allowLineBreaks={true}
            />
          </div>
        </div>

        {/* Scroll Indicator */}
        {scrollProgress < 0.1 && (
          <div style={{
            position: 'fixed',
            bottom: isMobile ? '60px' : '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3,
            textAlign: 'center',
            animation: 'bounce 2s infinite'
          }}>
            <div style={{
              width: isMobile ? '24px' : '30px',
              height: isMobile ? '40px' : '50px',
              border: '2px solid #D7B377',
              borderRadius: isMobile ? '12px' : '15px',
              position: 'relative',
              margin: '0 auto 10px'
            }}>
              <div style={{
                width: '4px',
                height: isMobile ? '8px' : '10px',
                backgroundColor: '#D7B377',
                borderRadius: '2px',
                position: 'absolute',
                top: isMobile ? '6px' : '8px',
                left: '50%',
                transform: 'translateX(-50%)',
                animation: 'scroll-wheel 2s infinite'
              }} />
            </div>
            <p style={{ 
              color: '#D7B377', 
              fontSize: isMobile ? '0.8rem' : '0.9rem', 
              opacity: 0.7 
            }}>
              Scroll to begin
            </p>
          </div>
        )}

        {/* Skip Button */}
        <div style={{
          position: 'fixed',
          top: isMobile ? '20px' : '30px',
          right: isMobile ? '15px' : '30px',
          zIndex: 4
        }}>
          <button
            onClick={onIntroComplete}
            style={{
              padding: isMobile ? '8px 16px' : '12px 24px',
              backgroundColor: 'rgba(215, 179, 119, 0.1)',
              border: '1px solid rgba(215, 179, 119, 0.4)',
              borderRadius: '8px',
              color: '#D7B377',
              fontSize: isMobile ? '12px' : '14px',
              cursor: 'pointer',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              opacity: 0.7
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(215, 179, 119, 0.2)';
              e.target.style.opacity = '1';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(215, 179, 119, 0.1)';
              e.target.style.opacity = '0.7';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Skip Intro ✕
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          40% {
            transform: translateX(-50%) translateY(-20px);
          }
          60% {
            transform: translateX(-50%) translateY(-10px);
          }
        }
        
        @keyframes scroll-wheel {
          0% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(15px);
            opacity: 0;
          }
        }
        
        @keyframes golden-shimmer {
          0%, 100% {
            filter: brightness(1);
          }
          50% {
            filter: brightness(1.2);
          }
        }
        
        @keyframes gradient-shift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        @keyframes float-particles {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
            opacity: 0.3;
          }
          33% {
            transform: translateY(-20px) rotate(120deg);
            opacity: 0.6;
          }
          66% {
            transform: translateY(10px) rotate(240deg);
            opacity: 0.4;
          }
        }
        
        @media (max-width: 768px) {
          .voxsmyth-intro-text {
            font-size: 2rem !important;
            padding: 0 10px !important;
            line-height: 1.3 !important;
          }
          
          .voxsmyth-intro-text.large {
            font-size: 2.5rem !important;
          }
          
          .voxsmyth-intro-text.medium {
            font-size: 2.2rem !important;
          }
          
          .voxsmyth-intro-text.small {
            font-size: 1.8rem !important;
          }
        }
        
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </>
  );
};

// Golden text component with handwriting animation and line break support
const GoldenText = ({ text, progress, fontSize = '3rem', textAlign = 'center', allowLineBreaks = false }) => {
  // Handle line breaks if allowLineBreaks is true
  const lines = allowLineBreaks ? text.split('\n') : [text];
  
  // Mobile-responsive font sizing
  const isMobileDevice = window.innerWidth < 768;
  const responsiveFontSize = isMobileDevice ? 
    (fontSize === '4rem' ? '1.8rem' :  // Slightly increased for better readability
     fontSize === '3.5rem' ? '1.6rem' :  
     fontSize === '3rem' ? '1.4rem' : 
     fontSize === '2.5rem' ? '1.8rem' :  // Make section 1 text bigger
     fontSize === '2.2rem' ? '1.5rem' : fontSize) : fontSize;
  
  return (
    <div style={{
      fontSize: responsiveFontSize,
      fontFamily: '"DM Serif Display", serif',
      color: '#D7B377',
      textAlign,
      lineHeight: isMobileDevice && allowLineBreaks ? 1.1 : (isMobileDevice ? 1.3 : 1.2),
      textShadow: '0 0 20px rgba(215, 179, 119, 0.5), 0 0 40px rgba(215, 179, 119, 0.3)',
      position: 'relative',
      maxWidth: isMobileDevice ? '95vw' : '90vw',
      padding: isMobileDevice ? '0 15px' : '0 20px',
      wordBreak: 'normal',
      whiteSpace: 'normal',
      overflow: 'visible'
    }}>
      {lines.map((line, lineIndex) => {
        const words = line.split(' ');
        return (
          <div key={lineIndex} style={{ 
            marginBottom: allowLineBreaks && lineIndex < lines.length - 1 ? '0.2em' : '0' 
          }}>
            {words.map((word, wordIndex) => {
              // Adjust word delay to account for multiple lines
              const totalWordIndex = lines.slice(0, lineIndex).reduce((acc, prevLine) => 
                acc + prevLine.split(' ').length, 0) + wordIndex;
              const wordDelay = totalWordIndex * 0.1;
              const wordProgress = Math.max(0, Math.min(1, (progress - wordDelay) * 4));
              
              return (
                <span key={`${lineIndex}-${wordIndex}`} style={{ display: 'inline-block', marginRight: '0.3em' }}>
                  {word.split('').map((letter, letterIndex) => {
                    const letterDelay = 0.05;
                    const letterProgress = Math.max(0, Math.min(1, 
                      (wordProgress - (letterIndex * letterDelay)) / letterDelay
                    ));
                    
                    // Calculate animation delay once to avoid conflicts
                    const animationDelayValue = `${totalWordIndex * 0.1 + letterIndex * 0.05}s`;
                    
                    return (
                      <span
                        key={letterIndex}
                        style={{
                          display: 'inline-block',
                          opacity: letterProgress,
                          transform: `translateY(${(1 - letterProgress) * 10}px)`,
                          transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
                          ...(letterProgress > 0.5 && {
                            animation: `golden-shimmer 2s ease-in-out infinite ${animationDelayValue}`
                          })
                        }}
                      >
                        {letter}
                      </span>
                    );
                  })}
                </span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default memo(VoxSmythScrollIntro);